<template>
  <div class="modal fade bd-example-modal-lg" id="reportSummarModal" tabindex="-1" role="dialog" ref="modalRef"
    aria-labelledby="exampleModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body">
          <div class="d-flex">
            <h3 class="col-md-12 mt-4">Transfer Plan Summary Report</h3>
            <button type="button" class="close text-right close-button" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <hr />

          <div v-if="!loading" class="report">
            <h4>
              {{ sourceSchool }} ➜ 
              {{ destinationSchool }}<span v-if="reportData?.destination_program" class="dest-program-inline">
                ({{ reportData.destination_program }})
              </span>
            </h4>
            <!-- Summary -->
            <div class="summary-box">
              <div>
                <strong>
                  <h5>{{ sourceSchool }}:</h5>
                </strong>
                <div class="transfer-total">Total Courses : {{ totalCourses }} </div>
                <div class="transfer-good">Transferable: {{ transferredCount }} </div>
                <div class="transfer-bad">Not Transferable: {{ notTransferredCount }} </div>
              </div>

              <div class="mt-2">
                <div class="d-flex justify-content-between small">
                  <span>Transfer rate</span>
                  <span>{{ transferRate.toFixed(1) }}%</span>
                </div>
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar bg-success" role="progressbar" :style="{ width: transferRate + '%' }"
                    :aria-valuenow="transferRate" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
              </div>
            </div>

            <!-- Quick KPIs -->
            <div class="row mb-3">
              <div class="col-6 col-md-3 mb-2">
                <div class="kpi">
                  <div class="kpi-title">Total Source Credits</div>
                  <div class="kpi-value">{{ formatNum(eligibleCredits) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3 mb-2">
                <div class="kpi">
                  <div class="kpi-title">Transferable Credits</div>
                  <div class="kpi-value text-success transfer-good">{{ formatNum(totalTransferredCredits) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3 mb-2">
                <div class="kpi">
                  <div class="kpi-title">Not Transferable Credits</div>
                  <div class="kpi-value text-danger transfer-bad">{{ formatNum(totalCreditLost) }}</div>
                </div>
              </div>
              <div class="col-6 col-md-3 mb-2">
                <div class="kpi">
                  <div class="kpi-title">Dest. Credits</div>
                  <div class="kpi-value">{{ formatNum(totalDestinationCredits) }}</div>
                </div>
              </div>
            </div>

            <!-- Controls (apply to Pima section) -->
            <div class="card p-3 mb-3" style="border: 1px solid #e7eaf0;">
              <div class="controls-grid">
                <div class="control">
                  <label class="control-label">Status</label>
                  <div class="btn-group btn-group-sm" role="group">
                    <button type="button" class="btn"
                      :class="statusFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'"
                      @click="statusFilter = 'all'">
                      All ({{ totalCourses }})
                    </button>
                    <button type="button" class="btn"
                      :class="statusFilter === 'transferred' ? 'btn-success' : 'btn-outline-success'"
                      @click="statusFilter = 'transferred'">
                      Transferable ({{ transferredCount }})
                    </button>
                    <button type="button" class="btn"
                      :class="statusFilter === 'not' ? 'btn-danger' : 'btn-outline-danger'"
                      @click="statusFilter = 'not'">
                      Non-transferable ({{ notTransferredCount }})
                    </button>
                  </div>
                </div>

                <div class="control">
                  <label class="control-label">Requirement</label>
                  <select class="form-control form-control-sm" v-model="requirementFilter">
                    <option value="">All requirements</option>
                    <option v-for="req in requirements" :key="req" :value="req">{{ getRequirementDisplayName(req) }}
                    </option>
                  </select>
                </div>

                <div class="control">
                  <label class="control-label">Search</label>
                  <input v-model.trim="search" type="text" class="form-control form-control-sm"
                    placeholder="Search course, transfer, or requirement…" />
                </div>
              </div>
            </div>

            <!-- SECTION 1: Pima → University of Arizona (Courses) -->
            <h2><span class="bluefont">{{ sourceSchool }}</span> Courses</h2>
            <table>
              <thead>
                <tr>
                  <th>PIMA COURSE</th>
                  <th class="num">CREDITS</th>
                  <th>UNIVERSITY OF ARIZONA MAPPING</th>
                  <th>UNIVERSITY OF ARIZONA REQUIREMENT</th>
                  <th>PIMA REQUIREMENT</th>

                </tr>
              </thead>
              <tbody>
                <tr v-for="c in filteredSortedCourses" :key="'pima:' + c.sourceCode">
                  <td>{{ formatSourceCourseName(c.sourceCourse) }}</td>
                  <td class="num">{{ formatNum(c.sourceCredit) }}</td>
                  <td>
                    <span class="badge" :class="c.transferrable ? 'badge-success' : 'badge-danger'">
                      {{ formatUaMapping(c) }}
                    </span>
                  </td>
                  <td>
                    <div v-if="formatRequirementLines(c.requirement).length">
                      <div v-for="(r, i) in formatRequirementLines(c.requirement)" :key="'dest:' + i">
                        • {{ r }}
                      </div>
                    </div>
                    <span v-else-if="c.transferrable && isElectiveMapping(c)">• {{ additionalCreditsLabel }}</span>
                    <span v-else-if="c.transferrable" class="badge badge-warning">Not Applicable</span>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <div v-if="formatRequirementLines(c.sourceRequirement).length">
                      <div v-for="(r, i) in formatRequirementLines(c.sourceRequirement)" :key="'src:' + i">
                        • {{ r }}
                      </div>
                    </div>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr v-if="filteredSortedCourses.length === 0">
                  <td colspan="5" class="text-center text-muted">No mappings</td>
                </tr>
              </tbody>
            </table>

            <!-- Destination courses  -->
            <h2><span class="bluefont">{{ destinationSchool }}</span> Courses</h2>
            <table>
              <thead>
                <tr>
                  <th>Destination Course</th>
                  <th class="num">Credits</th>
                  <th>Requirement(s)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in destinationCourseRows" :key="row.key">
                  <td>
                    {{ formatSourceCourseName(row.course) }}
                    <span v-if="row.isMulti" class="badge badge-success ml-2">Multiple requirements</span>
                  </td>
                  <td class="num">{{ formatNum(row.credit) }}</td>
                  <td>
                    <div v-if="row.requirements.length">
                      <div v-for="(r, i) in row.requirements" :key="i">• {{ getRequirementDisplayName(r) }}</div>
                    </div>
                    <span v-else>—</span>
                  </td>
                </tr>
                <tr v-if="destinationCourseRows.length === 0">
                  <td colspan="3" class="text-center text-muted">No destination courses found</td>
                </tr>
              </tbody>
            </table>
            <!-- Narrative Summary -->
            <h2>Summary & Notes</h2>
            <p class="mb-2">
              From <strong>{{ sourceSchool }}</strong> to <strong>{{ destinationSchool }}</strong>,
              we analyzed <strong>{{ totalCourses }}</strong> courses totaling
              <strong>{{ formatNum(eligibleCredits) }}</strong> credits. The overall transfer rate is
              <strong>{{ transferRate.toFixed(1) }}%</strong> with
              <span class="transfer-good">{{ transferredCount }} transferable</span> and
              <span class="transfer-bad">{{ notTransferredCount }} not transferable</span>.
            </p>

            <div class="mb-2">
              <strong>Note:</strong>
              This plan represents one valid pathway to complete your degree. Alternative course selections or sequences may also satisfy degree requirements. Consult your academic advisor to review your options and develop the plan that best supports your academic goals.
            </div>
          </div>

          <div class="no-results text-center" v-if="loading">
            <div class="loader"></div>
          </div>
        </div>

        <div class="text-center modal-footer">
          <button type="button" class="btn btn-secondary btn-close" data-dismiss="modal">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, PropType } from "vue";

type TransferMapRow = {
  source_courses: string[];
  destination_course: string;
  destination_credit: number;
  credit_loss: number;
  requirement?: string | null;
};

type TransferReport = {
  destination_program?: string;
  generic_elective_source_codes?: string[];
  transfer_data?: {
    source_school?: string;
    destination_school?: string;
    credit_count?: number;
    total_transfered_courses_credit_lost?: number;
    source_data?: any[];
    transfer_data?: any[];
    destination_data?: any[];
  };
  destination_school?: string;
  destination_data?: {
    destination_data?: any[];
    credit_count?: number;
    source_school?: string;
    destination_school?: string;
  };
  source_data?: {
    source_data?: any[];
    source_school?: string;
  };
};

type CourseRow = {
  sourceCourse: string;
  sourceCode: string;
  destinationCourse: string;
  sourceCredit: number;
  destinationCredit: number;
  requirement?: string;
  sourceRequirement?: string;
  transferrable: boolean;
};

export default defineComponent({
  name: "ReportSummaryModal",
  props: {
    reportData: { type: Object as PropType<TransferReport>, required: true },
  },
  setup(props) {
    const loading = ref(false);

    function getCourseCode(s: string): string {
      const str = String(s ?? "").toUpperCase().trim();
      const m = str.match(/^[A-Z&]{2,6}\s*\d{1,4}[A-Z]{0,3}/);
      if (!m) return str;
      const raw = m[0].replace(/\s+/, " ").trim();
      return raw.replace(/^([A-Z&]{2,6})\s*(\d)/, "$1 $2");
    }

    const sourceSchool = computed(
      () =>
        props.reportData?.source_data?.source_school ??
        props.reportData?.transfer_data?.source_school ??
        props.reportData?.destination_data?.source_school ??
        "—"
    );
    const destinationSchool = computed(
      () =>
        props.reportData?.destination_school ??
        props.reportData?.destination_data?.destination_school ??
        props.reportData?.transfer_data?.destination_school ??
        "—"
    );
    const totalDestinationCredits = computed(
      () => Number(props.reportData?.destination_data?.credit_count ?? 0)
    );

    // Taken (dedupe to one row per code).
    // `source_data.source_data` is populated by the Julia `transfer_plan_report`
    // with `get_source_degree=true`, so each row carries the source-school
    // requirement string the course satisfies — we read it directly from here
    // for the "Pima requirement" column rather than threading it through
    // the transfer rows.
    const rawTaken = computed<any[]>(
      () => props.reportData?.source_data?.source_data ?? []
    );
    const takenByCode = computed(() => {
      const map = new Map<
        string,
        { name: string; credit: number; sourceRequirement: string }
      >();
      for (const c of rawTaken.value) {
        const code = getCourseCode(c?.course ?? "");
        if (!code) continue;
        const name = String(c?.course ?? "").trim() || code;
        const credit = Number(c?.credit ?? 0);
        const sourceRequirement = String(c?.requirement ?? "") || "";
        const prev = map.get(code);
        if (!prev || name.length > prev.name.length) {
          map.set(code, { name, credit, sourceRequirement });
        } else if (!prev.sourceRequirement && sourceRequirement) {
          // Keep the longer name we already chose, but backfill a missing
          // requirement string from another duplicate row.
          prev.sourceRequirement = sourceRequirement;
        }
      }
      return map;
    });

    // Mapping (authoritative)
    const transferRows = computed<TransferMapRow[]>(
      () => props.reportData?.transfer_data?.transfer_data ?? []
    );

    const genericElectiveSourceCodes = computed(() => {
      const codes = props.reportData?.generic_elective_source_codes ?? [];
      return new Set(codes.map((c) => getCourseCode(c)).filter(Boolean));
    });

    function isGenericElectiveSource(row: CourseRow): boolean {
      return genericElectiveSourceCodes.value.has(row.sourceCode) ||
             /^ELCR\s/i.test(row.sourceCode);
    }

    const mapBySourceCode = computed(() => {
      const map = new Map<string, { dest: string; destCredit: number; requirement: string }>();
      for (const t of transferRows.value) {
        const dest = String(t?.destination_course ?? "");
        const destCredit = Number(t?.destination_credit ?? 0);
        const req = String(t?.requirement ?? "") || "";
        for (const s of t?.source_courses ?? []) {
          const code = getCourseCode(s);
          if (code && !map.has(code)) map.set(code, { dest, destCredit, requirement: req });
        }
      }
      return map;
    });

    const allCourseRows = computed<CourseRow[]>(() => {
      const rows: CourseRow[] = [];
      for (const [code, info] of takenByCode.value.entries()) {
        const m = mapBySourceCode.value.get(code);
        const isGeneric = genericElectiveSourceCodes.value.has(code) || /^ELCR\s/i.test(code);
        rows.push({
          sourceCourse: info.name,
          sourceCode: code,
          destinationCourse: isGeneric ? "Elective" : (m?.dest || "—"),
          sourceCredit: Number(info.credit || 0),
          destinationCredit: m?.destCredit ? Number(m.destCredit) : 0,
          requirement: m?.requirement || "",
          sourceRequirement: info.sourceRequirement || "",
          transferrable: isGeneric || Boolean(m?.dest),
        });
      }
      const seen = new Set<string>();
      const unique: CourseRow[] = [];
      for (const r of rows) {
        const k = `${r.sourceCode}→${(r.destinationCourse || "—").toUpperCase()}`;
        if (!seen.has(k)) { seen.add(k); unique.push(r); }
      }
      return unique;
    });

    // Filters / sorting (apply to Pima section)
    const statusFilter = ref<"all" | "transferred" | "not">("all");
    const requirementFilter = ref<string>("");
    const search = ref<string>("");
    const sortKey = ref<"course" | "credit">("course");
    const sortDir = ref<"asc" | "desc">("asc");

    const filteredSortedCourses = computed(() => {
      let rows = allCourseRows.value.slice();

      if (statusFilter.value !== "all") {
        const want = statusFilter.value === "transferred";
        rows = rows.filter((r) => r.transferrable === want);
      }
      if (requirementFilter.value) {
        rows = rows.filter((r) => (r.requirement || "") === requirementFilter.value);
      }

      const q = (search.value || "").toLowerCase();
      if (q) {
        rows = rows.filter(
          (r) =>
            r.sourceCourse.toLowerCase().includes(q) ||
            (r.destinationCourse || "").toLowerCase().includes(q) ||
            (r.requirement || "").toLowerCase().includes(q) ||
            (r.sourceRequirement || "").toLowerCase().includes(q)
        );
      }

      rows.sort((a, b) => {
        const dir = sortDir.value === "asc" ? 1 : -1;
        return sortKey.value === "course"
          ? a.sourceCourse.localeCompare(b.sourceCourse) * dir
          : (a.sourceCredit - b.sourceCredit) * dir;
      });

      return rows;
    });

    const requirements = computed(() => {
      const set = new Set(
        allCourseRows.value.map((c) => (c.requirement || "").trim()).filter(Boolean)
      );
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    });

    function splitRequirements(req?: string): string[] {
      const s = String(req || "").trim();
      if (!s) return [];

      // The Julia backend joins requirement names with a single space and
      // appends an individuated id like `(R532/L20)_C_3` to each one. Walk the
      // string forward, treating everything up to (and including) each id as
      // one token; any trailing chunk without an id (e.g. "AGEC Completion")
      // is captured as its own final token.
      const idRe = /\(R\d+\/L\d+\)(?:_[A-Za-z0-9_]+)?/g;
      const tokens: string[] = [];
      let lastEnd = 0;
      let m: RegExpExecArray | null;
      while ((m = idRe.exec(s)) !== null) {
        const end = m.index + m[0].length;
        const chunk = s.slice(lastEnd, end).trim();
        if (chunk) tokens.push(chunk);
        lastEnd = end;
      }
      if (lastEnd < s.length) {
        const tail = s.slice(lastEnd).trim();
        if (tail) tokens.push(tail);
      }
      if (tokens.length) return tokens;

      const alt = s.split(/\s{2,}|\s\|\s/g).map(x => x.trim()).filter(Boolean);
      return alt.length ? alt : [s];
    }

    function formatRequirementLines(req?: string | null): string[] {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const raw of splitRequirements(req ?? "")) {
        const display = getRequirementDisplayName(raw).trim();
        if (display && !seen.has(display)) {
          seen.add(display);
          out.push(display);
        }
      }
      return out;
    }

   const destinationCourseRows = computed(() => {
      const a = (props.reportData?.transfer_data?.destination_data ?? []) as Array<{ course: string; credit: number; requirement?: string }>;
      const b = (props.reportData?.destination_data?.destination_data ?? []) as Array<{ course: string; credit: number; requirement?: string }>;
      const byKey = new Map<string, { course: string; credit: number; requirements: Set<string> }>();
      for (const row of [...a, ...b]) {
        const course = String(row?.course ?? "").trim();
        const credit = Number(row?.credit ?? 0);
        const key = `${course}§${credit}`;
        if (!byKey.has(key)) byKey.set(key, { course, credit, requirements: new Set<string>() });
        splitRequirements(row?.requirement).forEach(p => byKey.get(key)!.requirements.add(p));
      }
      const out = Array.from(byKey.entries()).map(([key, v]) => ({
        key, course: v.course, credit: v.credit,
        requirements: Array.from(v.requirements),
        isMulti: v.requirements.size > 1
      }));
      out.sort((x, y) => x.course.localeCompare(y.course));
      return out;
    });

    // KPIs
    const totalCourses = computed(() => allCourseRows.value.length);
    const transferredCount = computed(() => allCourseRows.value.filter(c => c.transferrable).length);
    const notTransferredCount = computed(() => allCourseRows.value.filter(c => !c.transferrable).length);
    const eligibleCredits = computed(() => allCourseRows.value.reduce((s, c) => s + (c.sourceCredit || 0), 0));
    const totalTransferredCredits = computed(() =>
      allCourseRows.value.filter(c => c.transferrable).reduce((s, c) => s + (c.sourceCredit || 0), 0)
    );
    const totalCreditLost = computed(() => eligibleCredits.value - totalTransferredCredits.value);
    const transferRate = computed(() =>
      totalCourses.value ? (transferredCount.value / totalCourses.value) * 100 : 0
    );

    function formatNum(n: number) {
      return Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 });
    }
    function toggleSort(key: "course" | "credit") {
      if (sortKey.value === key) sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
      else { sortKey.value = key; sortDir.value = "asc"; }
    }
    function sortIndicator(key: "course" | "credit") {
      if (sortKey.value !== key) return "";
      return sortDir.value === "asc" ? "▲" : "▼";
    }
    function getRequirementDisplayName(reqName: string): string {
      let name = reqName;

      // Strip off requirement id at the end: e.g. "Something (ID_XYZ)"
      const reqIdMatch = name.match(/\(\S+\)(_\S+)*$/);
      if (reqIdMatch) {
        name = name.slice(0, reqIdMatch.index).trim();
      }

      // Strip off requirement index at start: e.g. "1. Some requirement"
      const reqIndexMatch = name.match(/^\d+\./);
      if (reqIndexMatch) {
        name = name.slice(reqIndexMatch[0].length).trim();
      }

      // Strip off OR at start
      if (name.startsWith("OR")) {
        name = name.slice(2).trim();
      }

      // Strip off AND at start
      if (name.startsWith("AND")) {
        name = name.slice(3).trim();
      }

      // Replace underscores with spaces (e.g. "AGEC_B_OPTIONS_HUMFA_COURSES")
      name = name.replace(/_/g, " ");

      return name;
    }

    function stripGenericCourseCode(s: string): string {
      // If starts with "ELCR DIGITS " — ELCR is always a generic elective prefix
      if (/^ELCR\s/i.test(s)) {
        const rest = s.replace(/^ELCR\s+\d+\s+/i, "");
        if (rest) return rest;
      }
      // If starts with any prefix followed by a 5+ digit number (not a real course code)
      const longNum = s.replace(/^[A-Za-z&]{2,8}\s+\d{5,}\s+/i, "");
      if (longNum !== s) return longNum;
      return s;
    }

    function formatSourceCourseName(name: string): string {
      const s = stripGenericCourseCode(String(name || "").trim());
      return getRequirementDisplayName(s);
    }

    // Scan destination course requirements to find "Additional Credits to Achieve X Credits"
    // so we always show the correct program credit total (120 or 128, etc.)
    const additionalCreditsLabel = computed(() => {
      const allReqs = [
        ...(props.reportData?.transfer_data?.destination_data ?? []),
        ...(props.reportData?.destination_data?.destination_data ?? []),
        ...(props.reportData?.transfer_data?.transfer_data ?? []),
      ] as Array<{ requirement?: string }>;
      for (const row of allReqs) {
        const m = String(row?.requirement ?? "").match(/additional credits to achieve (\d+) credits/i);
        if (m) return `Additional Credits to Achieve ${m[1]} Credits`;
      }
      return "Additional Credits";
    });

    function isElectiveMapping(c: CourseRow): boolean {
      if (isGenericElectiveSource(c)) return true;
      const dest = String(c.destinationCourse ?? "").trim();
      // 1TR courses (e.g. "HIST 1TR", "ELCR 1TR") or destination ELCR/Free Elective
      return /1TR$/i.test(dest) || /^ELCR\s/i.test(dest) || /free elective/i.test(dest);
    }

    function formatUaMapping(c: CourseRow): string {
      if (isGenericElectiveSource(c)) return "Elective";
      if (!c.transferrable) return "No equivalency";
      const dest = String(c.destinationCourse ?? "").trim();
      if (/^Transferrable$/i.test(dest)) return "Transferrable";
      return dest ? `→ ${dest}` : "No equivalency";
    }

    return {
      loading,
      sourceSchool, destinationSchool,
      totalDestinationCredits, totalCreditLost, totalTransferredCredits, totalCourses,
      transferredCount, notTransferredCount, eligibleCredits, transferRate,
      statusFilter, requirementFilter, search, requirements,
      sortKey, sortDir, toggleSort, sortIndicator, getRequirementDisplayName,
      formatRequirementLines, formatUaMapping, formatSourceCourseName,
      filteredSortedCourses, destinationCourseRows,
      additionalCreditsLabel, isElectiveMapping,
      formatNum
    };
  },
});
</script>


<style>
/* --- Modal polish --- */
.modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  overflow: hidden;
}

.modal-body {
  padding: 1.25rem 1.25rem 1rem;
}

.modal-footer {
  border-top: 1px solid #e7eaf0;
}

hr {
  margin: 0.75rem 0 1rem;
}

h4 {
  font-weight: 600;
  font-size: 1.2rem;
  letter-spacing: 0.2px;
  margin: 0;
}

/* Close buttons */
.close-button span,
.btn-close {
  font-size: 1.25rem;
  color: #475569;
  transition: color .15s ease;
}

.close-button:hover span,
.btn-close:hover {
  color: #0f172a;
}

/* --- Loader --- */
.loader {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 4px solid #eef2f7;
  border-top-color: #0d6efd;
  margin: 16px auto;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* --- Report base --- */
.report {
  font-size: 14px;
  color: #1f2937;
  line-height: 1.6;
  overflow-x: hidden;
  max-width: 100%;
}

/* ---- Compact controls that fit on one row ---- */
:root {
  --control-h: 30px;
}

.report .form-control-sm {
  height: var(--control-h) !important;
  padding: .2rem .5rem;
  line-height: 1.3;
  box-sizing: border-box;
}

.report .btn-group {
  display: flex;
  gap: 4px;
  align-items: stretch;
  width: 100%;
flex-wrap: wrap;}

.report .btn-group-sm>.btn {
  height: var(--control-h);
  padding: 0 .4rem;
  font-size: .7rem;
  line-height: 1.3;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.report .controls-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;
  align-items: end;
}

@media (max-width: 992px) {
  .report .controls-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 576px) {
  .report .controls-grid {
    grid-template-columns: 1fr;
  }
}

/* KPI cards */
.kpi {
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: 0 3px 10px rgba(16, 24, 40, 0.04);
}

.kpi-title {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

/* --- Tables --- */
.report table {
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
  font-variant-numeric: tabular-nums;
  margin-bottom: 16px;
}

.report thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
  font-weight: 600;
  color: #111827;
  background: linear-gradient(#f8fafc, #f3f6fb);
  border-bottom: 1px solid #e1e6ef;
  padding: 12px 14px;
  white-space: normal;
  overflow-wrap: anywhere;
}

.report tbody td {
  padding: 12px 14px;
  color: #334155;
  vertical-align: top;
  border-bottom: 1px solid #eef2f7;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.report tbody tr:nth-child(even) td {
  background: #fbfdff;
}

.report tbody tr:hover td {
  background: #f5f9ff;
}

.report td:first-child,
.report th:first-child {
  padding-left: 16px;
}

.report tbody td:first-child {
  font-weight: 500;
  color: #111827;
}

.report table tr:last-child td {
  border-bottom: 0;
}

.report thead th:first-child {
  border-top-left-radius: 12px;
}

.report thead th:last-child {
  border-top-right-radius: 12px;
}

.report tbody tr:last-child td:first-child {
  border-bottom-left-radius: 12px;
}

.report tbody tr:last-child td:last-child {
  border-bottom-right-radius: 12px;
}

.report td.num,
.report th.num {
  text-align: right;
}

/* Callout colors */
.report .transfer-total {
  font-weight: 600;
}

.report .transfer-good {
  color: #15803d;
  font-weight: 600;
}

.report .transfer-bad {
  color: #dc2626;
  font-weight: 600;
}

.badge-success {
  background-color: #16a34a;
}

.badge-danger {
  background-color: #dc2626;
}

.badge-warning {
  background-color: #ea7317;
}

.badge {
  color: #fff;
  padding: .35rem .5rem;
  border-radius: 999px;
  font-weight: 600;
}

.bluefont {
  color: #281c86;
  font-weight: 800;
}

/* Hover */
.report tbody tr {
  transition: background-color 0.15s ease;
}

.report tbody tr:hover td {
  background: #f1f5ff;
}

/* Modal bg */
.modal-body {
  background: #fafafa;
}

.modal-footer {
  background: #f9fafb;
}

/* Summary box */
.summary-box {
  border: 1px solid #e0e7ff;
  border-left: 6px solid #0d6efd;
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 1rem 0 1.5rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, .05);
}
.dest-program-inline {
  margin-left: 8px;
  color: #64748b;
  font-weight: 600;
  font-size: 0.95em;
}
/* Wider report modal */
#reportSummarModal .modal-dialog.modal-lg {
  max-width: 95vw;
  width: 95vw;
}

@media (min-width: 1600px) {
  #reportSummarModal .modal-dialog.modal-lg {
    max-width: 1500px;
  }
}
/* Fix report table without horizontal scroll */
#reportSummarModal .report {
  overflow-x: hidden;
  max-width: 100%;
}

#reportSummarModal .report table {
  width: 100%;
  max-width: 100%;
  min-width: 0 !important;
  table-layout: fixed;
}

#reportSummarModal .report th,
#reportSummarModal .report td {
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* Fix long green/red badges so they don't cover next columns */
#reportSummarModal .report .badge {
  white-space: normal !important;
  display: inline-block;
  max-width: 100%;
  line-height: 1.25;
  text-align: left;
  border-radius: 8px;
}

/* better column widths for first table */
#reportSummarModal .report table:first-of-type th:nth-child(1),
#reportSummarModal .report table:first-of-type td:nth-child(1) {
  width: 26%;
}

#reportSummarModal .report table:first-of-type th:nth-child(2),
#reportSummarModal .report table:first-of-type td:nth-child(2) {
  width: 7%;
}

#reportSummarModal .report table:first-of-type th:nth-child(3),
#reportSummarModal .report table:first-of-type td:nth-child(3) {
  width: 22%;
}

#reportSummarModal .report table:first-of-type th:nth-child(4),
#reportSummarModal .report table:first-of-type td:nth-child(4) {
  width: 23%;
}

#reportSummarModal .report table:first-of-type th:nth-child(5),
#reportSummarModal .report table:first-of-type td:nth-child(5) {
  width: 22%;
}
.pathway-disclaimer-box {
  margin-top: 18px;
  margin-bottom: 8px;
  padding: 14px 20px;
  background: #eef3f6;
  border: 1px solid #9fc2ff;
  color: #31485f;
  border-radius: 6px;
  text-align: center;
  font-size: 15px;
  line-height: 1.5;
  width: 100%;
  display: block;
  box-sizing: border-box;
}
</style>
